import { isOffRegister } from '../register-status.js';
import { escapeHtml, titleCase } from '../ui.js';

// Issue Sheet / Document Receipt Sheet — generated ON DEMAND from the live
// register and printed via the browser (Save as PDF). Print-perfect A4 with
// the ASP title block; no inline scripts (CSP) — the opener calls print().

const fmtD = (v) => {
  const d = v && typeof v.toDate === 'function' ? v.toDate() : (v ? new Date(v) : null);
  return d && !isNaN(d) ? d.toLocaleDateString('en-GB') : '—';
};

export function openDocSheet({ kind, project, clientName, docs, generatedBy }) {
  const isIssue = kind === 'issue';
  const title = isIssue ? 'Issue Sheet' : 'Document Receipt Sheet';
  const rows = docs
    .slice()
    .sort((a, b) => String(a.docNumber || a.filename || '').localeCompare(String(b.docNumber || b.filename || '')));

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(project.number || '')} — ${title}</title>
  <style>
    @page { size: A4; margin: 16mm; }
    * { box-sizing: border-box; }
    body { font: 10.5pt/1.45 'Helvetica Neue', Arial, sans-serif; color: #0C1139; margin: 0; }
    .head { display: flex; justify-content: space-between; border-bottom: 2px solid #0C1139; padding-bottom: 10px; }
    .brand { --weight-wordmark: 700; font-size: 20pt; font-weight: var(--weight-wordmark); letter-spacing: -0.055em; display: flex; align-items: center; gap: 9px; }
    .brand span { color: #F5385D; }
    .brand svg { width: 19pt; height: 19pt; flex: none; }
    .brand-ref { font-family: ui-monospace, Menlo, monospace; font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: #55564F; margin-bottom: 3px; }
    .contact { font-size: 7.5pt; color: #55564F; text-align: right; line-height: 1.5; }
    .block { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #0C1139; margin-top: 14px; }
    .cell { padding: 6px 10px; border-top: 1px solid #0C1139; }
    .cell:nth-child(-n+2) { border-top: none; }
    .cell + .cell { border-left: 1px solid #0C1139; }
    .lbl { font-size: 6.5pt; letter-spacing: 0.1em; text-transform: uppercase; color: #55564F; }
    .val { font-size: 10pt; font-weight: 600; }
    h1 { font-size: 13pt; margin: 18px 0 8px; letter-spacing: 0.02em; }
    table { width: 100%; border-collapse: collapse; margin-top: 4px; }
    th { font-size: 7pt; letter-spacing: 0.1em; text-transform: uppercase; color: #55564F; text-align: left;
      border-bottom: 1.5px solid #0C1139; padding: 5px 6px; }
    td { font-size: 9pt; padding: 6px; border-bottom: 0.5px solid #C7C8C3; vertical-align: top; }
    .mono { font-family: 'Courier New', monospace; font-size: 8.5pt; }
    .sup { color: #C0290A; font-weight: 600; }
    .cur { color: #1F7A4D; font-weight: 600; }
    .rev-flag { color: #FF5001; font-weight: 600; }
    .foot { margin-top: 22px; font-size: 7.5pt; color: #55564F; display: flex; justify-content: space-between; }
  </style></head><body>
  <div class="head">
    <div class="brand"><svg viewBox="0 0 64 64" aria-hidden="true"><path d="M8 22 H56" stroke="#0C1139" stroke-width="10" stroke-linecap="round" fill="none"></path><path d="M8 42 H36" stroke="#0C1139" stroke-width="10" stroke-linecap="round" fill="none"></path><circle cx="56" cy="42" r="7" fill="#F5385D"></circle></svg>Track<span>.</span></div>
    <div class="contact"><div class="brand-ref">ASP · ${escapeHtml(project.number || '')}</div>+44 (0) 1530 561230 · aspconsulting.co.uk · info@aspconsulting.co.uk<br>
      Point One, Tournament Way, Ashby-de-la-Zouch, United Kingdom</div>
  </div>
  <div class="block">
    <div class="cell"><div class="lbl">Client</div><div class="val">${escapeHtml(clientName || '—')}</div></div>
    <div class="cell"><div class="lbl">Project</div><div class="val">${escapeHtml(project.number || '')} — ${escapeHtml(project.title || '')}</div></div>
    <div class="cell"><div class="lbl">Document</div><div class="val">${title}</div></div>
    <div class="cell"><div class="lbl">Generated</div><div class="val">${new Date().toLocaleDateString('en-GB')} · ${escapeHtml(titleCase(generatedBy || ''))}</div></div>
  </div>
  <h1>${title.toUpperCase()} — ${rows.length} DOCUMENT${rows.length === 1 ? '' : 'S'}</h1>
  <table>
    <thead><tr>
      <th style="width:20%">Doc No</th><th style="width:7%">Rev</th><th>Description / Filename</th>
      <th style="width:10%">Category</th><th style="width:11%">${isIssue ? 'Issued' : 'Received'}</th>
      <th style="width:16%">${isIssue ? 'To' : 'From'}</th><th style="width:10%">Status</th>
    </tr></thead>
    <tbody>
      ${rows.map((d) => `<tr>
        <td class="mono">${escapeHtml(d.docNumber || '—')}</td>
        <td class="mono">${escapeHtml(d.revision || '—')}</td>
        <td>${escapeHtml(d.filename || '—')}${d.needsReview ? ' <span class="rev-flag">⚑ review</span>' : ''}</td>
        <td>${escapeHtml(titleCase(d.category || '—'))}</td>
        <td class="mono">${fmtD(d.date)}</td>
        <td>${escapeHtml(isIssue ? (d.to || '—') : (d.from || '—'))}</td>
        <td class="${isOffRegister(d.status) ? 'sup' : 'cur'}">${escapeHtml((d.status || 'current').toUpperCase())}</td>
      </tr>`).join('')}
    </tbody>
  </table>
  <div class="foot">
    <span>Generated from the live Track document register — supersession per ASP document control standard.</span>
    <span>ASP Engineering UK Limited · Registered in England &amp; Wales 03438155</span>
  </div>
  </body></html>`;

  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(html); // trusted template; all data escaped above
  w.document.close();
  setTimeout(() => { try { w.focus(); w.print(); } catch (e) { /* user can print manually */ } }, 500);
}
